import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useClient } from './useClient'
import { NavigationValidationService } from '../services/navigationValidationService'
import { emitUserPlanCountUpdated } from '../services/userPlanCountService'
import { notifyWarningCustom, notifyWarningCustom2 } from '../utils/notify'
import { usePaywall } from '../contexts/PaywallContext/usePaywall'
import { useArtsLimitUpsell } from '../contexts/ArtsLimitUpsellContext/useArtsLimitUpsell'
import { useCampaignSidebar } from '../contexts/CampaignSidebarContext/useCampaignSidebar'

const PAYWALL_REASONS = [
  'campaign_limit_reached',
  'campaign_trial_unavailable',
  'campaign_art_balance_low',
  'art_limit_reached',
  'art_generation_limit_reached',
]

const ARTS_LIMIT_UPSELL_REASONS = [
  'art_limit_reached',
  'art_generation_limit_reached',
]

/**
 * useNavigateWithValidation
 *
 * Hook que gerencia estado de UI (loading, modals, toasts) e executa navegação
 * quando validações são aprovadas.
 *
 * Uso:
 *   const { handleNavigate, loading, blockDowngrade, setBlockDowngrade } = useNavigateWithValidation()
 *   <button onClick={() => handleNavigate('/ChatSocket')}>Campaign</button>
 */
export const useNavigateWithValidation = () => {
  const navigation = useNavigate()
  const { t } = useTranslation()
  const { clientData } = useClient()
  const { openPaywall } = usePaywall()
  const { openArtsLimitUpsell } = useArtsLimitUpsell()
  const { requestCreateCampaign } = useCampaignSidebar()

  const [loading, setLoading] = useState(false)
  const [blockDowngrade, setBlockDowngrade] = useState(false)

  const handleCloseDowngradeModal = () => {
    setBlockDowngrade(false)
  }

  const handleNavigate = async (route) => {
    setLoading(true)

    try {
      const result = await NavigationValidationService.validateNavigation(route, {
        clientData,
      })

      emitUserPlanCountUpdated()

      if (!result.canNavigate) {
        if (ARTS_LIMIT_UPSELL_REASONS.includes(result.reason)) {
          await openArtsLimitUpsell({
            returnTo: route,
            source: 'sidebar_navigation',
            reason: result.reason,
          })
          setLoading(false)
          return
        }

        if (PAYWALL_REASONS.includes(result.reason)) {
          openPaywall({
            reason: result.reason,
            returnTo: route,
          })
          setLoading(false)
          return
        }

        if (result.showDowngradeModal) {
          setBlockDowngrade(true)
          setLoading(false)
          return
        }

        if (result.message) {
          notifyWarningCustom(result.message)
          setLoading(false)
          return
        }

        if (result.messageKey) {
          notifyWarningCustom(t(result.messageKey))
          setLoading(false)
          return
        }

        if (result.showPlanLimitToast) {
          notifyWarningCustom2(
            t(result.planLimitToastMessageKey),
            <div
              onClick={() => navigation('/PlanCheckoutUser')}
              style={{
                fontSize: 12,
                fontWeight: 300,
                fontFamily: 'Poppins',
                cursor: 'pointer',
                backgroundColor: '#7A7E00',
                color: '#f2f486',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                padding: '0px 16px',
                margin: '0px 0px',
                borderRadius: 10,
                height: 30,
              }}
            >
              {t('plans.user_plan_count_post_change_plan_button', 'Mudar Plano')}
            </div>
          )
          setLoading(false)
          return
        }

        setLoading(false)
        return
      }
      if (route.toLowerCase() === '/chatsocket') {
        await requestCreateCampaign()
        return
      }

      navigation(route, {
        state: result.navigationState || undefined,
      })
    } catch (error) {
      console.error('Navigation validation error:', error)
    } finally {
      setLoading(false)
    }
  }

  return {
    handleNavigate,
    loading,
    blockDowngrade,
    setBlockDowngrade,
    handleCloseDowngradeModal,
  }
}
